import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Info, Power, ToggleRight, type LucideIcon } from "lucide-react";
import type { AppWithAvailability, FlagRow } from "@gwrctl/api-client";
import type { FlagKey, FlagToggle } from "@gwrctl/apps-registry";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { AppIcon } from "../components/AppIcon.tsx";
import {
  Banner,
  Button,
  Card,
  Chips,
  ConfirmButton,
  Dot,
  Empty,
  ErrorState,
  Field,
  toneColor,
  type Tone,
} from "../components/Ui.tsx";

/**
 * Feature flag / bakım modu / zorunlu güncelleme / duyuru.
 *
 * Değerli olan kısım: bozuk bir özelliği yayın beklemeden kapatmak. OTA yeni paket
 * indirtir; flag ise uygulama açılışında ya da en geç birkaç dakika içinde etki eder
 * (uygulamalar açılışta ve öne gelince en sık 5 dk'da bir çeker, sunucu 30 sn
 * önbellekler). "Anında" değil; bu yüzden metinlerde de öyle yazıyor.
 *
 * Sayfa ham anahtar/değer yerine sözleşmedeki anahtarlar için okunur kartlar çizer:
 * yanlış biçimli bir değer (ör. id'siz duyuru) mobilde sessizce hiçbir şey yapmıyordu.
 * Ham editör "Gelişmiş" altında duruyor.
 */

const EFFECT = "Uygulama açılışında ya da en geç birkaç dakika içinde etki eder.";
const PENDING =
  "Bu uygulamada flag desteği kodda var ama henüz yayınlanmış bir sürümde yok; yeni sürüm/OTA çıkana kadar etkisiz.";

// Sunucudaki doğrulamayla aynı: istemci baştan uyarsın, 400'e düşmeden.
const KEY_RE = /^[A-Za-z][A-Za-z0-9._-]{0,63}$/;
const VERSION_RE = /^\d+\.\d+\.\d+$/;

const LEVELS = ["info", "success", "warning"] as const;
type Level = (typeof LEVELS)[number];
const LEVEL_LABELS: Record<Level, string> = { info: "bilgi", success: "başarılı", warning: "uyarı" };
const LEVEL_STYLE: Record<Level, { tone: Tone; icon: LucideIcon }> = {
  info: { tone: "accent", icon: Info },
  success: { tone: "ok", icon: CheckCircle2 },
  warning: { tone: "warn", icon: AlertTriangle },
};

const AUDIENCES = ["all", "premium", "free"] as const;
type Audience = (typeof AUDIENCES)[number];
const AUDIENCE_LABELS: Record<Audience, string> = { all: "herkes", premium: "premium", free: "ücretsiz" };

const FEATURE_AUDIENCES = ["off", "beta", "premium_only", "premium", "all"] as const;
type FeatureAudience = (typeof FEATURE_AUDIENCES)[number];
const FEATURE_LABELS: Record<FeatureAudience, string> = {
  off: "kapalı",
  beta: "beta",
  premium_only: "yalnız premium",
  premium: "premium kilitli",
  all: "herkes",
};
const FEATURE_HINTS: Record<FeatureAudience, string> = {
  off: "kimse görmez; bozuk özelliği kapatmak için",
  beta: "yalnızca beta kanalı, premium şartsız",
  premium_only: "yalnızca premium görür; ücretsizde hiç yok",
  premium: "herkes görür, premium kullanır; ücretsizde paywall",
  all: "herkes kullanır",
};

// --- Değer ayrıştırma ------------------------------------------------------
// Kayıtlı değerler eski biçimlerde olabilir (maintenance: true, duyuru {tr,en,level});
// hiçbiri sayfayı kırmamalı, okunabildiği kadarı gösterilmeli.

type Loc = { tr?: string; en?: string };

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

function asLoc(v: unknown): Loc {
  if (typeof v === "string") return v.trim() ? { tr: v } : {};
  if (!isObj(v)) return {};
  const out: Loc = {};
  if (typeof v.tr === "string" && v.tr.trim()) out.tr = v.tr;
  if (typeof v.en === "string" && v.en.trim()) out.en = v.en;
  return out;
}
const hasLoc = (l: Loc): boolean => Boolean(l.tr || l.en);

/** Boş dilleri atar; ikisi de boşsa undefined (alan hiç gönderilmez). */
function locPayload(tr: string, en: string): Loc | undefined {
  const out: Loc = {};
  if (tr.trim()) out.tr = tr.trim();
  if (en.trim()) out.en = en.trim();
  return hasLoc(out) ? out : undefined;
}

/**
 * OTA kanalı hedefi: yalnızca birden fazla kanalı olan uygulamalarda (Raadiocast:
 * beta + production) seçilir. Uygulama kendi kanalıyla karşılaştırır; kanalı
 * bilmeyen eski sürüm hedefi yok sayıp herkese gösterir.
 */
type Channel = "all" | "beta" | "production";
const CHANNELS: readonly Channel[] = ["all", "beta", "production"];
const CHANNEL_LABELS: Record<Channel, string> = { all: "Tüm kanallar", beta: "Yalnızca beta", production: "Yalnızca üretim" };
const asChannel = (v: unknown): Channel => (CHANNELS.includes(v as Channel) ? (v as Channel) : "all");

function parseMaintenance(v: unknown): { enabled: boolean; message: Loc; channel: Channel } {
  if (v === true || v === "true") return { enabled: true, message: {}, channel: "all" };
  if (isObj(v)) {
    // `enabled` yoksa eski biçim: nesnenin varlığı "açık" demekti.
    const enabled = "enabled" in v ? v.enabled === true : true;
    return { enabled, message: asLoc("message" in v ? v.message : v), channel: asChannel(v.channel) };
  }
  return { enabled: false, message: {}, channel: "all" };
}

interface Announcement {
  id: string | null;
  audience: Audience;
  channel: Channel;
  title: Loc;
  message: Loc;
  level: Level;
}

function parseAnnouncement(v: unknown): Announcement | null {
  if (typeof v === "string") {
    return v.trim() ? { id: null, audience: "all", channel: "all", title: {}, message: { tr: v }, level: "info" } : null;
  }
  if (!isObj(v)) return null;
  const title = asLoc(v.title);
  // Eski şerit biçiminde metin doğrudan {tr,en} olarak nesnenin kökündeydi.
  const message = asLoc("message" in v ? v.message : v);
  if (!hasLoc(title) && !hasLoc(message)) return null;
  return {
    id: typeof v.id === "string" && v.id.trim() ? v.id : null,
    audience: AUDIENCES.includes(v.audience as Audience) ? (v.audience as Audience) : "all",
    channel: asChannel(v.channel),
    title,
    message,
    level: LEVELS.includes(v.level as Level) ? (v.level as Level) : "info",
  };
}

function tryJson(text: string): { ok: true; value: unknown } | { ok: false } {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false };
  }
}

/**
 * Duyuru kimliği: uygulama her duyuruyu cihaz başına id'de YALNIZCA bir kez gösterir.
 * Sabit bir id ile yeni duyuru yazılırsa onu görmüş cihazlar yenisini hiç görmez;
 * bu yüzden her yayında zamandan yeni bir id üretilir. Aynı dakikada ikinci yayında
 * saniye eklenir ki yine farklı olsun.
 */
function announcementId(previous: string | null): string {
  const d = new Date();
  const p = (n: number): string => String(n).padStart(2, "0");
  const base = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
  return base === previous ? `${base}${p(d.getSeconds())}` : base;
}

// --- Uygulama seçimi -------------------------------------------------------

const APP_KEY = "gwrctl.flags.app";

// Depolama kapalı/gizli pencerede fırlatabilir; tercih hatırlanmazsa sorun değil.
function readApp(): string | null {
  try {
    return localStorage.getItem(APP_KEY);
  } catch {
    return null;
  }
}
function writeApp(id: string): void {
  try {
    localStorage.setItem(APP_KEY, id);
  } catch {
    /* hatırlanmaz */
  }
}

// --- Yazma -----------------------------------------------------------------

/**
 * Her kart kendi mutation'ını kullanır: bir kartın "kaydediliyor" durumu diğer
 * kartların düğmelerini dönen göstergeye çevirmesin.
 */
function useFlagWrite(appId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["flags", appId] });
  };
  const set = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown; done: string }) =>
      client.setFlag(appId, key, value),
    onSuccess: (_res, { done }) => {
      refresh();
      toast({ tone: "ok", title: done, body: EFFECT });
    },
    onError: (err, { key }) =>
      toast({ tone: "danger", title: `${key} kaydedilemedi`, body: humanizeError(err, "Kaydedilemedi") }),
  });
  const remove = useMutation({
    mutationFn: ({ key }: { key: string; done: string }) => client.deleteFlag(appId, key),
    onSuccess: (_res, { done }) => {
      refresh();
      toast({ tone: "ok", title: done, body: EFFECT });
    },
    onError: (err, { key }) =>
      toast({ tone: "danger", title: `${key} silinemedi`, body: humanizeError(err, "Silinemedi") }),
  });
  return { set, remove };
}

// --- Sayfa -----------------------------------------------------------------

export function FlagsPage(): ReactNode {
  const [pickedApp, setPickedApp] = useState<string | null>(readApp);
  const apps = useQuery({ queryKey: ["apps"], queryFn: () => client.apps() });

  // Yalnızca flag okuyan uygulamalar; Discord botu gibi olmayanlar listede boş kart olurdu.
  const flagApps = (apps.data ?? []).filter((a) => a.flags);
  const app = flagApps.find((a) => a.id === pickedApp) ?? flagApps[0];

  return (
    <>
      <PageHeader kicker="yönetim" title="Feature flag" meta={EFFECT} />

      <PageBody>
        {apps.isError ? (
          <Card>
            <ErrorState error={apps.error} onRetry={() => void apps.refetch()} />
          </Card>
        ) : apps.isLoading ? (
          <Card>
            <Empty>Yükleniyor…</Empty>
          </Card>
        ) : !app ? (
          <Card>
            <Empty>Flag destekleyen uygulama yok</Empty>
          </Card>
        ) : (
          <>
            <div role="group" aria-label="Uygulama" className="flex flex-wrap gap-2">
              {flagApps.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="chip inline-flex items-center gap-2"
                  aria-pressed={a.id === app.id}
                  onClick={() => {
                    setPickedApp(a.id);
                    writeApp(a.id);
                  }}
                >
                  <AppIcon id={a.id} name={a.displayName} size={18} />
                  {a.displayName}
                  {a.flags?.pendingRelease && <Dot tone="idle" />}
                </button>
              ))}
            </div>
            {/* key: uygulama değişince tüm taslak formlar sıfırlansın, başka uygulamaya gitmesin. */}
            <AppFlags key={app.id} app={app} />
          </>
        )}
      </PageBody>
    </>
  );
}

function AppFlags({ app }: { app: AppWithAvailability }): ReactNode {
  const cfg = app.flags!;
  const flags = useQuery({
    queryKey: ["flags", app.id],
    queryFn: () => client.flags(app.id),
  });
  const has = (k: FlagKey): boolean => cfg.keys.includes(k);
  const pendingKey = (k: FlagKey): boolean => !cfg.pendingRelease && Boolean(cfg.pendingKeys?.includes(k));
  const multiChannel = (app.ota?.channels?.length ?? 0) > 1;

  if (flags.isError) {
    // Hata varken "kapalı" göstermek yanlış güven verir; kartları hiç çizme.
    return (
      <Card>
        <ErrorState error={flags.error} onRetry={() => void flags.refetch()} />
      </Card>
    );
  }
  if (flags.isLoading || !flags.data) {
    return (
      <Card>
        <Empty>Yükleniyor…</Empty>
      </Card>
    );
  }

  const rows = flags.data;
  const byKey = new Map(rows.map((r) => [r.key, r]));

  return (
    <>
      {cfg.pendingRelease && (
        <Banner tone="info" kicker="henüz yayında değil">
          {PENDING}
        </Banner>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {has("maintenance") && (
          <MaintenanceCard
            appId={app.id}
            row={byKey.get("maintenance")}
            pending={pendingKey("maintenance")}
            multiChannel={multiChannel}
          />
        )}
        {has("minSupportedVersion") && (
          <VersionCard
            appId={app.id}
            row={byKey.get("minSupportedVersion")}
            pending={pendingKey("minSupportedVersion")}
          />
        )}
      </div>

      {has("announcement") && (
        <AnnouncementCard
          appId={app.id}
          row={byKey.get("announcement")}
          hasPremium={Boolean(cfg.hasPremium)}
          pending={pendingKey("announcement")}
          multiChannel={multiChannel}
        />
      )}

      {cfg.toggles && cfg.toggles.length > 0 && (
        <TogglesCard appId={app.id} toggles={cfg.toggles} byKey={byKey} />
      )}

      {has("feature") && (
        <FeaturesCard
          appId={app.id}
          rows={rows.filter((r) => r.key.startsWith("feature."))}
          known={cfg.features ?? []}
          pending={pendingKey("feature")}
        />
      )}

      <AdvancedCard appId={app.id} appName={app.displayName} rows={rows} />
    </>
  );
}

function PendingNote({ show }: { show: boolean }): ReactNode {
  return show ? (
    <Banner tone="info" kicker="henüz yayında değil">
      {PENDING}
    </Banner>
  ) : null;
}

function StatePill({ on, onLabel, offLabel }: { on: boolean; onLabel: string; offLabel: string }): ReactNode {
  const color = on ? toneColor.warn : toneColor.idle;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-[2px] text-2xs font-medium"
      style={{ color, background: `color-mix(in srgb, ${color} 13%, transparent)` }}
    >
      <Dot tone={on ? "warn" : "idle"} glow={on} />
      {on ? onLabel : offLabel}
    </span>
  );
}

function LocLines({ loc, empty }: { loc: Loc; empty: string }): ReactNode {
  if (!hasLoc(loc)) return <p className="text-xs text-neutral-600">{empty}</p>;
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs">
      {loc.tr && (
        <>
          <dt className="mono text-neutral-600">TR</dt>
          <dd className="break-words text-neutral-200">{loc.tr}</dd>
        </>
      )}
      {loc.en && (
        <>
          <dt className="mono text-neutral-600">EN</dt>
          <dd className="break-words text-neutral-200">{loc.en}</dd>
        </>
      )}
    </dl>
  );
}

// --- Bakım modu ------------------------------------------------------------

function MaintenanceCard({
  appId,
  row,
  pending,
  multiChannel,
}: {
  appId: string;
  row?: FlagRow;
  pending: boolean;
  multiChannel: boolean;
}): ReactNode {
  const state = parseMaintenance(row?.value);
  const { set } = useFlagWrite(appId);
  const [tr, setTr] = useState(state.message.tr ?? "");
  const [en, setEn] = useState(state.message.en ?? "");
  const [channel, setChannel] = useState<Channel>(state.channel);
  const target = multiChannel ? channel : "all";

  const payload = (): unknown => {
    const message = locPayload(tr, en);
    return {
      enabled: true,
      ...(message ? { message } : {}),
      ...(target !== "all" ? { channel: target } : {}),
    };
  };

  return (
    <Card
      title="Bakım modu"
      icon={Power}
      action={
        <StatePill
          on={state.enabled}
          onLabel={state.channel === "all" ? "açık" : `açık · ${state.channel === "beta" ? "beta" : "üretim"}`}
          offLabel="kapalı"
        />
      }
    >
      <div className="grid gap-3">
        <PendingNote show={pending} />
        <p className="text-xs text-neutral-500">
          Açıkken uygulama yalnızca tam ekran bakım ekranını gösterir; mesaj boşsa varsayılan metin çıkar.
        </p>
        {multiChannel && (
          <Field label="Kanal" hint="Önce beta'da denemek için: yalnızca o OTA kanalındaki kurulumlar bakıma girer.">
            <Chips values={CHANNELS} value={channel} onChange={setChannel} labels={CHANNEL_LABELS} />
          </Field>
        )}
        {state.enabled && (
          <div className="grid gap-1.5">
            <span className="label mb-0">Şu anki mesaj</span>
            <LocLines loc={state.message} empty="Varsayılan bakım metni gösteriliyor." />
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Mesaj (TR) · isteğe bağlı">
            <textarea className="input" rows={2} value={tr} onChange={(e) => setTr(e.target.value)} />
          </Field>
          <Field label="Mesaj (EN) · isteğe bağlı">
            <textarea className="input" rows={2} value={en} onChange={(e) => setEn(e.target.value)} />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {state.enabled ? (
            <>
              {/* Kapatmak kullanıcıyı geri getirir, riski yok: tek tık. */}
              <Button
                variant="primary"
                loading={set.isPending && set.variables?.value === false}
                onClick={() => set.mutate({ key: "maintenance", value: false, done: "Bakım modu kapatıldı" })}
              >
                Bakım modunu kapat
              </Button>
              <ConfirmButton
                variant="secondary"
                confirmLabel="Mesaj güncellensin?"
                loading={set.isPending && set.variables?.value !== false}
                onConfirm={() => set.mutate({ key: "maintenance", value: payload(), done: "Bakım mesajı güncellendi" })}
              >
                Mesajı güncelle
              </ConfirmButton>
            </>
          ) : (
            <ConfirmButton
              variant="danger"
              confirmLabel={target === "all" ? "Herkes için açılsın mı?" : `${CHANNEL_LABELS[target]} için açılsın mı?`}
              loading={set.isPending}
              onConfirm={() => set.mutate({ key: "maintenance", value: payload(), done: "Bakım modu açıldı" })}
            >
              Bakım modunu aç
            </ConfirmButton>
          )}
        </div>
      </div>
    </Card>
  );
}

// --- Zorunlu güncelleme ----------------------------------------------------

function VersionCard({ appId, row, pending }: { appId: string; row?: FlagRow; pending: boolean }): ReactNode {
  const current = typeof row?.value === "string" ? row.value : null;
  const { set, remove } = useFlagWrite(appId);
  const [draft, setDraft] = useState(current ?? "");
  const value = draft.trim();
  const valid = VERSION_RE.test(value);

  return (
    <Card
      title="Zorunlu güncelleme"
      icon={AlertTriangle}
      action={<StatePill on={Boolean(current)} onLabel={`≥ ${current}`} offLabel="kapalı" />}
    >
      <div className="grid gap-3">
        <PendingNote show={pending} />
        {row && !current && (
          <Banner tone="warn">Kayıtlı değer okunamadı ({JSON.stringify(row.value)}); Gelişmiş'ten bak.</Banner>
        )}
        <p className="text-xs text-neutral-500">
          En düşük desteklenen sürüm:{" "}
          <span className="mono text-neutral-200">{current ?? "kapalı"}</span>
        </p>
        <Field
          label="En düşük sürüm (x.y.z)"
          error={value && !valid ? "Biçim x.y.z olmalı, ör. 2.6.1" : undefined}
          hint="Uygulamanın sürüm adı (versionName), versionCode değil."
        >
          <input
            className="input mono"
            inputMode="decimal"
            placeholder="ör. 2.6.1"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </Field>
        <Banner tone="warn" kicker="engeller">
          Bu sürümün altındaki kurulumlar, Play Store'dan güncelleyene kadar uygulamayı kullanamaz.
        </Banner>
        <div className="flex flex-wrap items-center gap-2">
          <ConfirmButton
            variant="primary"
            confirmLabel="Eskiler engellensin mi?"
            disabled={!valid || value === current}
            loading={set.isPending}
            onConfirm={() =>
              set.mutate({ key: "minSupportedVersion", value, done: `En düşük sürüm ${value} oldu` })
            }
          >
            Uygula
          </ConfirmButton>
          {row && (
            <ConfirmButton
              variant="ghost"
              confirmLabel="Kaldırılsın mı?"
              loading={remove.isPending}
              onConfirm={() => remove.mutate({ key: "minSupportedVersion", done: "Zorunlu güncelleme kaldırıldı" })}
            >
              Kaldır
            </ConfirmButton>
          )}
        </div>
      </div>
    </Card>
  );
}

// --- Aç/kapa anahtarları -----------------------------------------------------

function TogglesCard({
  appId,
  toggles,
  byKey,
}: {
  appId: string;
  toggles: readonly FlagToggle[];
  byKey: Map<string, FlagRow>;
}): ReactNode {
  const { set, remove } = useFlagWrite(appId);

  return (
    <Card title="Anahtarlar" icon={ToggleRight} hint="uygulamaya özel · kayıt yoksa kapalı">
      <div className="grid gap-3">
        {toggles.map((t) => {
          const row = byKey.get(t.key);
          const on = row?.value === true;
          return (
            <div key={t.key} className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-neutral-100">{t.label}</p>
                <p className="text-xs text-neutral-500">
                  <span className="mono">{t.key}</span>
                  {t.hint ? ` · ${t.hint}` : ""}
                </p>
                {row && !on && row.value !== false && (
                  <p className="text-xs text-amber-400">
                    Kayıtlı değer true değil ({JSON.stringify(row.value)}) → uygulama kapalı sayar.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <StatePill on={on} onLabel="açık" offLabel="kapalı" />
                {on ? (
                  <ConfirmButton
                    variant="ghost"
                    confirmLabel="Kapatılsın mı?"
                    loading={remove.isPending}
                    onConfirm={() => remove.mutate({ key: t.key, done: `${t.label} kapatıldı` })}
                  >
                    Kapat
                  </ConfirmButton>
                ) : (
                  <ConfirmButton
                    variant="primary"
                    confirmLabel="Açılsın mı?"
                    loading={set.isPending}
                    onConfirm={() => set.mutate({ key: t.key, value: true, done: `${t.label} açıldı` })}
                  >
                    Aç
                  </ConfirmButton>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// --- Duyuru ----------------------------------------------------------------

function AnnouncementCard({
  appId,
  row,
  hasPremium,
  pending,
  multiChannel,
}: {
  appId: string;
  row?: FlagRow;
  hasPremium: boolean;
  pending: boolean;
  multiChannel: boolean;
}): ReactNode {
  const current = row ? parseAnnouncement(row.value) : null;
  const { set, remove } = useFlagWrite(appId);
  const [titleTr, setTitleTr] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [msgTr, setMsgTr] = useState("");
  const [msgEn, setMsgEn] = useState("");
  const [level, setLevel] = useState<Level>("info");
  const [audience, setAudience] = useState<Audience>("all");
  const [channel, setChannel] = useState<Channel>("all");
  const [lang, setLang] = useState<"tr" | "en">("tr");
  const target = multiChannel ? channel : "all";

  const title = locPayload(titleTr, titleEn);
  const message = locPayload(msgTr, msgEn);
  const ready = Boolean(title || message);

  function fillFrom(a: Announcement): void {
    setTitleTr(a.title.tr ?? "");
    setTitleEn(a.title.en ?? "");
    setMsgTr(a.message.tr ?? "");
    setMsgEn(a.message.en ?? "");
    setLevel(a.level);
    setAudience(hasPremium ? a.audience : "all");
    setChannel(a.channel);
  }

  function publish(): void {
    const value: Record<string, unknown> = {
      id: announcementId(current?.id ?? null),
      // Premium'u olmayan uygulamalar yalnızca "all" gösterir; başka kitle hiç görünmezdi.
      audience: hasPremium ? audience : "all",
      level,
      ...(target !== "all" ? { channel: target } : {}),
    };
    if (title) value.title = title;
    if (message) value.message = message;
    set.mutate({ key: "announcement", value, done: "Duyuru yayınlandı" });
  }

  return (
    <Card title="Duyuru" icon={Info} action={<StatePill on={Boolean(current)} onLabel="aktif" offLabel="yok" />}>
      <div className="grid gap-4">
        <PendingNote show={pending} />
        <p className="text-xs text-neutral-500">
          Her cihaz bir duyuruyu id başına yalnızca bir kez, diyalog olarak görür. Yeni yayında id otomatik
          yenilenir.
        </p>

        {/* Mevcut duyuru */}
        <div className="grid gap-2 rounded-[var(--radius-md)] border border-neutral-900 p-3">
          <span className="label mb-0">Şu anki duyuru</span>
          {!row ? (
            <p className="text-xs text-neutral-600">Aktif duyuru yok.</p>
          ) : !current ? (
            <p className="text-xs text-warn">Kayıtlı değer okunamadı; Gelişmiş'ten bak.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-neutral-500">
                <span>
                  id <span className="mono text-neutral-300">{current.id ?? "—"}</span>
                </span>
                <span>kitle: {AUDIENCE_LABELS[current.audience]}</span>
                {current.channel !== "all" && <span>kanal: {CHANNEL_LABELS[current.channel]}</span>}
                <span>seviye: {LEVEL_LABELS[current.level]}</span>
              </div>
              {!current.id && (
                <p className="text-2xs text-warn">
                  id yok (eski biçim): güncel uygulamalar bu duyuruyu göstermeyebilir; yeniden yayınla.
                </p>
              )}
              {current.title.tr || current.title.en ? (
                <p className="text-sm font-medium break-words text-neutral-100">
                  {current.title.tr ?? current.title.en}
                </p>
              ) : null}
              {current.message.tr || current.message.en ? (
                <p className="text-xs break-words text-neutral-300">{current.message.tr ?? current.message.en}</p>
              ) : null}
            </>
          )}
          {row && (
            <div className="flex flex-wrap gap-2">
              {current && (
                <Button size="sm" variant="ghost" onClick={() => fillFrom(current)}>
                  Forma kopyala
                </Button>
              )}
              <ConfirmButton
                size="sm"
                variant="ghost"
                confirmLabel="Kaldırılsın mı?"
                loading={remove.isPending}
                onConfirm={() => remove.mutate({ key: "announcement", done: "Duyuru kaldırıldı" })}
              >
                Kaldır
              </ConfirmButton>
            </div>
          )}
        </div>

        {/* Yeni duyuru + önizleme */}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="grid content-start gap-3">
            <span className="label mb-0">Yeni duyuru</span>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Başlık (TR)">
                <input className="input" value={titleTr} maxLength={80} onChange={(e) => setTitleTr(e.target.value)} />
              </Field>
              <Field label="Başlık (EN)">
                <input className="input" value={titleEn} maxLength={80} onChange={(e) => setTitleEn(e.target.value)} />
              </Field>
              <Field label="Mesaj (TR)">
                <textarea className="input" rows={3} value={msgTr} onChange={(e) => setMsgTr(e.target.value)} />
              </Field>
              <Field label="Mesaj (EN)">
                <textarea className="input" rows={3} value={msgEn} onChange={(e) => setMsgEn(e.target.value)} />
              </Field>
            </div>
            <Field label="Seviye">
              <Chips values={LEVELS} value={level} onChange={setLevel} labels={LEVEL_LABELS} />
            </Field>
            {hasPremium && (
              <Field label="Kitle">
                <Chips values={AUDIENCES} value={audience} onChange={setAudience} labels={AUDIENCE_LABELS} />
              </Field>
            )}
            {multiChannel && (
              <Field label="Kanal" hint="Kanalı bilmeyen eski uygulama sürümleri duyuruyu kanaldan bağımsız herkese gösterir.">
                <Chips values={CHANNELS} value={channel} onChange={setChannel} labels={CHANNEL_LABELS} />
              </Field>
            )}
            {!ready && (
              <p className="text-2xs text-neutral-600">En az bir başlık ya da mesaj yaz.</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <ConfirmButton
                variant="primary"
                confirmLabel={
                  target !== "all"
                    ? `${CHANNEL_LABELS[target]} kanalına?`
                    : hasPremium && audience !== "all"
                      ? `${AUDIENCE_LABELS[audience]} kullanıcılara?`
                      : "Herkese gösterilsin mi?"
                }
                disabled={!ready}
                loading={set.isPending}
                onConfirm={publish}
              >
                Yayınla
              </ConfirmButton>
              {current && (
                <span className="text-2xs text-neutral-600">Yayınlamak şu anki duyurunun yerine geçer.</span>
              )}
            </div>
          </div>

          <div className="grid content-start gap-2">
            <div className="flex items-center gap-2">
              <span className="label mb-0">Önizleme</span>
              <div className="ml-auto">
                <Chips values={["tr", "en"] as const} value={lang} onChange={setLang} labels={{ tr: "TR", en: "EN" }} />
              </div>
            </div>
            <AnnouncementPreview
              level={level}
              title={(lang === "tr" ? titleTr || titleEn : titleEn || titleTr).trim()}
              message={(lang === "tr" ? msgTr || msgEn : msgEn || msgTr).trim()}
              ok={lang === "tr" ? "Tamam" : "OK"}
            />
            <p className="text-2xs text-neutral-600">
              Seçilen dil boşsa uygulama diğer dile düşer; önizleme de öyle.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

/** Uygulamadaki diyalogun kaba bir taslağı: metin uzunluğu ve ton göz kararı görünsün. */
function AnnouncementPreview({
  level,
  title,
  message,
  ok,
}: {
  level: Level;
  title: string;
  message: string;
  ok: string;
}): ReactNode {
  const { tone, icon: Icon } = LEVEL_STYLE[level];
  const color = toneColor[tone];
  return (
    <div
      aria-hidden
      className="grid place-items-center rounded-[var(--radius-md)] border border-neutral-900 px-4 py-6"
      style={{ background: "rgba(8,9,15,0.55)" }}
    >
      <div
        className="grid w-full max-w-[280px] gap-2 rounded-[14px] border p-4"
        style={{ borderColor: `color-mix(in srgb, ${color} 30%, transparent)`, background: "var(--color-surface-2, #16171f)" }}
      >
        <div className="flex items-center gap-2">
          <Icon size={18} style={{ color }} className="shrink-0" />
          <span className="text-md font-semibold break-words text-neutral-100">
            {title || <span className="text-neutral-600">(başlık yok)</span>}
          </span>
        </div>
        {message ? (
          <p className="text-xs break-words whitespace-pre-wrap text-neutral-300">{message}</p>
        ) : (
          <p className="text-xs text-neutral-600">(mesaj yok)</p>
        )}
        <span
          className="mt-1 justify-self-end rounded-[8px] px-3 py-1 text-xs font-medium"
          style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)` }}
        >
          {ok}
        </span>
      </div>
    </div>
  );
}

// --- Özellikler ------------------------------------------------------------

const FEATURE_NAME_RE = /^[A-Za-z][A-Za-z0-9_-]{0,55}$/;

function FeaturesCard({
  appId,
  rows,
  known,
  pending,
}: {
  appId: string;
  rows: FlagRow[];
  known: readonly string[];
  pending: boolean;
}): ReactNode {
  const { set, remove } = useFlagWrite(appId);
  // Chips tıklaması hemen yazmaz: kullanıcıyı etkileyen her değişiklik onaydan geçsin.
  const [draft, setDraft] = useState<Record<string, FeatureAudience>>({});
  const [name, setName] = useState("");
  const [newAudience, setNewAudience] = useState<FeatureAudience>("beta");

  const existing = new Set(rows.map((r) => r.key));
  const newKey = `feature.${name.trim()}`;
  const nameValid = FEATURE_NAME_RE.test(name.trim());
  const suggestions = known.filter((f) => !existing.has(`feature.${f}`));

  return (
    <Card title="Özellikler" hint="feature.<ad> · kodda varsayılan kitle, flag onu ezer">
      <div className="grid gap-4">
        <PendingNote show={pending} />

        {rows.length === 0 ? (
          <p className="text-xs text-neutral-600">Flag'le ezilen özellik yok; hepsi koddaki varsayılanla çalışıyor.</p>
        ) : (
          <div className="grid gap-3">
            {rows.map((r) => {
              const saved = FEATURE_AUDIENCES.includes(r.value as FeatureAudience) ? (r.value as FeatureAudience) : null;
              const picked = draft[r.key] ?? saved ?? "off";
              const dirty = picked !== saved;
              return (
                <div key={r.key} className="grid gap-2 border-b border-neutral-900 pb-3 last:border-b-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="mono text-xs break-all text-neutral-100">{r.key.slice("feature.".length)}</span>
                    {!saved && (
                      <span className="text-2xs text-warn">tanınmayan değer: {JSON.stringify(r.value)}</span>
                    )}
                    <span className="ml-auto text-2xs text-neutral-600">
                      {new Date(r.updatedAt).toLocaleString("tr-TR")}
                    </span>
                  </div>
                  <Chips
                    values={FEATURE_AUDIENCES}
                    value={picked}
                    onChange={(v) => setDraft((d) => ({ ...d, [r.key]: v }))}
                    labels={FEATURE_LABELS}
                  />
                  <p className="text-2xs text-neutral-500">{FEATURE_HINTS[picked]}</p>
                  <div className="flex flex-wrap gap-2">
                    {dirty && (
                      <ConfirmButton
                        size="sm"
                        variant="primary"
                        confirmLabel="Uygulansın mı?"
                        loading={set.isPending && set.variables?.key === r.key}
                        onConfirm={() =>
                          set.mutate(
                            { key: r.key, value: picked, done: `${r.key} → ${FEATURE_LABELS[picked]}` },
                            {
                              onSuccess: () =>
                                setDraft((d) => {
                                  const { [r.key]: _drop, ...rest } = d;
                                  return rest;
                                }),
                            },
                          )
                        }
                      >
                        Uygula
                      </ConfirmButton>
                    )}
                    <ConfirmButton
                      size="sm"
                      variant="ghost"
                      confirmLabel="Varsayılana dönsün mü?"
                      loading={remove.isPending && remove.variables?.key === r.key}
                      onConfirm={() => remove.mutate({ key: r.key, done: `${r.key} koddaki varsayılana döndü` })}
                    >
                      Kaldır
                    </ConfirmButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="grid gap-3 rounded-[var(--radius-md)] border border-neutral-900 p-3">
          <span className="label mb-0">Özellik ekle</span>
          <Field
            label="Özellik adı"
            error={name.trim() && !nameValid ? "Harfle başlamalı; harf, rakam, _ ve - kullan." : undefined}
            hint="Uygulama kodu bu adı tanımalı (FEATURES); bilinmeyen bir ad hiçbir şey yapmaz."
          >
            <input
              className="input mono"
              list={`features-${appId}`}
              placeholder="ör. sleepTimerPlus"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <datalist id={`features-${appId}`}>
            {suggestions.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
          {suggestions.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 text-2xs text-neutral-500">
              Koddakiler:
              {suggestions.map((f) => (
                <button key={f} type="button" className="chip mono" onClick={() => setName(f)}>
                  {f}
                </button>
              ))}
            </div>
          ) : (
            known.length === 0 && (
              <p className="text-2xs text-neutral-600">Registry'de bu uygulama için tanımlı özellik adı yok.</p>
            )
          )}
          <Field label="Kitle">
            <Chips values={FEATURE_AUDIENCES} value={newAudience} onChange={setNewAudience} labels={FEATURE_LABELS} />
          </Field>
          <p className="text-2xs text-neutral-500">{FEATURE_HINTS[newAudience]}</p>
          <div>
            <ConfirmButton
              variant="primary"
              confirmLabel="Eklensin mi?"
              disabled={!nameValid || existing.has(newKey)}
              loading={set.isPending && set.variables?.key === newKey}
              onConfirm={() =>
                set.mutate(
                  { key: newKey, value: newAudience, done: `${newKey} eklendi` },
                  { onSuccess: () => setName("") },
                )
              }
            >
              Ekle
            </ConfirmButton>
            {existing.has(newKey) && (
              <span className="ml-2 text-2xs text-neutral-600">Zaten var; yukarıdan değiştir.</span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

// --- Gelişmiş ---------------------------------------------------------------

const RESERVED_KEYS = new Set(["maintenance", "minSupportedVersion", "announcement"]);

function AdvancedCard({ appId, appName, rows }: { appId: string; appName: string; rows: FlagRow[] }): ReactNode {
  const { set, remove } = useFlagWrite(appId);
  const [key, setKey] = useState("");
  const [text, setText] = useState("");

  const k = key.trim();
  const parsed = text.trim() ? tryJson(text) : null;
  const keyValid = KEY_RE.test(k);
  const reserved = RESERVED_KEYS.has(k) || k.startsWith("feature.");

  return (
    <Card flush>
      <details>
        <summary className="cursor-pointer px-4 py-3 text-sm text-neutral-300 hover:text-neutral-100">
          Gelişmiş · ham anahtar/değer ve {appName} flag listesi ({rows.length})
        </summary>
        <div className="grid gap-4 border-t border-neutral-900 p-4">
          <div className="grid gap-3">
            <Field
              label="Anahtar"
              error={k && !keyValid ? "Harfle başlamalı; harf, rakam, . _ - (en çok 64)." : undefined}
              hint={reserved ? "Bu anahtarın kartı yukarıda; sözleşmeye uymayan değer sunucuda reddedilir." : undefined}
            >
              <input className="input mono" placeholder="ör. myFlag" value={key} onChange={(e) => setKey(e.target.value)} />
            </Field>
            <Field
              label="Değer (JSON)"
              error={parsed && !parsed.ok ? "Geçerli JSON değil. Metin için tırnak kullan: \"merhaba\"" : undefined}
              hint={parsed?.ok ? "Geçerli JSON." : 'ör. true, 3, "metin", {"a":1}'}
            >
              <textarea
                className="input mono"
                rows={6}
                spellCheck={false}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </Field>
            <div className="flex flex-wrap items-center gap-2">
              <ConfirmButton
                variant="primary"
                confirmLabel="Kaydedilsin mi?"
                disabled={!keyValid || !parsed?.ok}
                loading={set.isPending}
                onConfirm={() => {
                  if (!parsed?.ok) return;
                  set.mutate({ key: k, value: parsed.value, done: `${k} kaydedildi` });
                }}
              >
                Kaydet
              </ConfirmButton>
              <span className="text-2xs text-neutral-600">{EFFECT}</span>
            </div>
          </div>

          <div className="grid gap-0 overflow-hidden rounded-[var(--radius-md)] border border-neutral-900">
            {rows.length === 0 ? (
              <Empty>Bu uygulama için flag tanımlı değil</Empty>
            ) : (
              rows.map((flag) => (
                <div key={flag.key} className="row flex-wrap items-start gap-3">
                  <div className="grid min-w-0 flex-1 basis-[240px] gap-1">
                    <span className="mono text-xs break-all text-neutral-100">{flag.key}</span>
                    <pre className="mono m-0 max-h-[200px] overflow-auto text-2xs break-all whitespace-pre-wrap text-accent-300">
                      {JSON.stringify(flag.value, null, 2)}
                    </pre>
                    <span className="text-2xs text-neutral-600">{new Date(flag.updatedAt).toLocaleString("tr-TR")}</span>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setKey(flag.key);
                        setText(JSON.stringify(flag.value, null, 2));
                      }}
                    >
                      Düzenle
                    </Button>
                    <ConfirmButton
                      size="sm"
                      variant="ghost"
                      confirmLabel="Silinsin mi?"
                      loading={remove.isPending && remove.variables?.key === flag.key}
                      onConfirm={() => remove.mutate({ key: flag.key, done: `${flag.key} silindi` })}
                    >
                      Sil
                    </ConfirmButton>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </details>
    </Card>
  );
}
